// src/pages/JournalEntries.jsx
// List of all journal entries with filters, status badges, and drill-down
// Role 2+ can view, approve, push to Zoho, export, reverse

import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import { AppDatePicker, AppSelect } from "../components/shared/FormControls";

const STATUS_META = {
  draft:            { label: "Draft",            color: "#455A64", bg: "#ECEFF1" },
  approved:         { label: "Approved",         color: "#1A7A4A", bg: "#E8F5EE" },
  pushed:           { label: "Pushed to Zoho",   color: "#1B3F6E", bg: "#EBF5FB" },
  push_failed:      { label: "Push failed",      color: "#B71C1C", bg: "#FFEBEE" },
  pending_approval: { label: "Needs approval",   color: "#C85A00", bg: "#FFF3E0" },
  reversed:         { label: "Reversed",         color: "#90A4AE", bg: "#F5F5F5" },
};

const TYPE_LABELS = {
  revenue:      "Revenue",
  expense:      "Expense",
  depreciation: "Depreciation",
  tds:          "TDS",
  tpa_settlement:"TPA settlement",
  bank_entry:   "Bank",
  reversal:     "Reversal",
};

export default function JournalEntries({ entity, user }) {
  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters,  setFilters]  = useState({ status: "", type: "", dateFrom: "", dateTo: "" });

  const isAdmin = user?.role === "iicipl_admin";
  const canApprove = ["accountant_staff", "iicipl_admin"].includes(user?.role);

  const load = useCallback(async () => {
    if (!entity?._id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });
      const res = await api.get(`/entities/${entity._id}/journal-entries?${params}`);
      setEntries(res.entries || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entity?._id, page, filters]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <JournalEntryDetail
      entry={selected} entity={entity} user={user}
      canApprove={canApprove} isAdmin={isAdmin}
      onBack={() => { setSelected(null); load(); }}
    />;
  }

  return (
    <div style={S.page}>

      <div style={S.pageHeader}>
        <h1 style={S.h1}>Journal entries</h1>
        <div style={{ fontSize: 13, color: "#90A4AE" }}>{total} entries total</div>
      </div>

      {/* Filters */}
      <div style={S.filterRow}>
        <AppSelect
          value={filters.status}
          onChange={value => setFilters(f => ({ ...f, status: value }))}
          options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))}
          placeholder="All statuses"
          isClearable
          style={{ minWidth: 180 }}
        />
        <AppSelect
          value={filters.type}
          onChange={value => setFilters(f => ({ ...f, type: value }))}
          options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          placeholder="All types"
          isClearable
          style={{ minWidth: 170 }}
        />
        <AppDatePicker value={filters.dateFrom} onChange={value => setFilters(f => ({ ...f, dateFrom: value }))} style={{ width: 150 }} />
        <span style={{ color: "#90A4AE", fontSize: 13 }}>to</span>
        <AppDatePicker value={filters.dateTo} onChange={value => setFilters(f => ({ ...f, dateTo: value }))} style={{ width: 150 }} />
        <button onClick={() => { setFilters({ status: "", type: "", dateFrom: "", dateTo: "" }); setPage(1); }} style={S.clearBtn}>Clear</button>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Date", "Journal No.", "Type", "Debit (₹)", "Credit (₹)", "Balanced", "Status", ""].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#90A4AE" }}>Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#90A4AE" }}>No entries found</td></tr>
            )}
            {entries.map(e => {
              const sm = STATUS_META[e.status] || STATUS_META.draft;
              return (
                <tr key={e._id} style={{ borderBottom: "1px solid #F0F4F8", cursor: "pointer" }}
                  onClick={() => setSelected(e)}>
                  <td style={S.td}>{fmtDate(e.journalDate)}</td>
                  <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12, color: "#1B3F6E" }}>{e.journalNumber}</td>
                  <td style={S.td}>{TYPE_LABELS[e.type] || e.type}</td>
                  <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace" }}>
                    {e.totalDebit?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace" }}>
                    {e.totalCredit?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    <span style={{ color: e.isBalanced ? "#1A7A4A" : "#B71C1C", fontWeight: 700 }}>
                      {e.isBalanced ? "✓" : "✗"}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...S.badge, background: sm.bg, color: sm.color }}>{sm.label}</span>
                  </td>
                  <td style={S.td}><span style={{ color: "#2E86AB", fontSize: 12 }}>View →</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 25 && (
        <div style={S.pagination}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={S.pageBtn}>← Prev</button>
          <span style={{ fontSize: 13, color: "#607D8B" }}>Page {page} of {Math.ceil(total / 25)}</span>
          <button disabled={page >= Math.ceil(total / 25)} onClick={() => setPage(p => p + 1)} style={S.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Journal Entry Detail ──────────────────────────────────────────────────────
function JournalEntryDetail({ entry, entity, user, canApprove, isAdmin, onBack }) {
  const [e,       setE]       = useState(entry);
  const [working, setWorking] = useState("");
  const [msg,     setMsg]     = useState("");
  const [error,   setError]   = useState("");

  const sm = STATUS_META[e.status] || STATUS_META.draft;

  async function approve() {
    setWorking("approve"); setError("");
    try {
      const res = await api.patch(`/entities/${entity._id}/journal-entries/${e._id}/approve`);
      setE(res.entry); setMsg("Entry approved.");
    } catch (err) { setError(err.message); }
    finally { setWorking(""); }
  }

  async function pushToZoho() {
    setWorking("push"); setError("");
    try {
      const res = await api.post(`/entities/${entity._id}/journal-entries/${e._id}/push-zoho`);
      setMsg("Pushed to Zoho Books. Journal ID: " + res.zohoJournalId);
      setE(prev => ({ ...prev, status: "pushed", zohoJournalId: res.zohoJournalId }));
    } catch (err) { setError(err.message); }
    finally { setWorking(""); }
  }

  async function reverse() {
    const input = window.prompt(
      `⚠ WARNING: This will create a reversal entry.\n\nThis action cannot be undone.\n\nType REVERSE (in capitals) to confirm:`
    );
    if (input !== "REVERSE") {
      if (input !== null) alert("Reversal cancelled. You must type exactly: REVERSE");
      return;
    }
    setWorking("reverse"); setError("");
    try {
      await api.post(`/entities/${entity._id}/journal-entries/${e._id}/reverse`);
      setMsg("Reversal entry created. Original entry marked as reversed.");
      setE(prev => ({ ...prev, status: "reversed" }));
    } catch (err) { setError(err.message); }
    finally { setWorking(""); }
  }

  const drLines = e.lineItems?.filter(l => l.debitOrCredit === "debit")  || [];
  const crLines = e.lineItems?.filter(l => l.debitOrCredit === "credit") || [];

  return (
    <div style={S.page}>
      <button onClick={onBack} style={S.backBtn}>← Back to list</button>

      <div style={S.detailCard}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: "#90A4AE", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>
              {TYPE_LABELS[e.type] || e.type}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1B3F6E", fontFamily: "monospace" }}>
              {e.journalNumber}
            </div>
            <div style={{ fontSize: 13, color: "#607D8B", marginTop: 4 }}>{fmtDate(e.journalDate)}</div>
          </div>
          <span style={{ ...S.badge, ...{ background: sm.bg, color: sm.color, fontSize: 13, padding: "6px 14px" } }}>
            {sm.label}
          </span>
        </div>

        {e.notes && <div style={{ fontSize: 13, color: "#455A64", marginBottom: 16, fontStyle: "italic" }}>{e.notes}</div>}

        {/* Line items */}
        <table style={{ ...S.table, marginBottom: 16 }}>
          <thead>
            <tr>
              {["Account", "Description", "Debit (₹)", "Credit (₹)"].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {e.lineItems?.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fafbfc" : "#fff", borderBottom: "1px solid #F0F4F8" }}>
                <td style={S.td}>{item.accountName}</td>
                <td style={{ ...S.td, color: "#607D8B", fontSize: 12 }}>{item.description}</td>
                <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace", color: "#1A7A4A" }}>
                  {item.debitOrCredit === "debit" ? item.amount?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace", color: "#B71C1C" }}>
                  {item.debitOrCredit === "credit" ? item.amount?.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}
                </td>
              </tr>
            ))}
            {/* Totals */}
            <tr style={{ background: "#EBF5FB", fontWeight: 700 }}>
              <td style={S.td} colSpan={2}>Total</td>
              <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace", color: "#1A7A4A" }}>
                {e.totalDebit?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </td>
              <td style={{ ...S.td, textAlign: "right", fontFamily: "monospace", color: "#B71C1C" }}>
                {e.totalCredit?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Balance check */}
        <div style={{
          padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: e.isBalanced ? "#E8F5EE" : "#FFEBEE",
          color: e.isBalanced ? "#1A7A4A" : "#B71C1C",
        }}>
          {e.isBalanced ? "✓ Entry is balanced — Dr = Cr" : "✗ Entry is NOT balanced — do not push to Zoho"}
        </div>

        {/* Zoho push info */}
        {e.zohoJournalId && (
          <div style={{ padding: "10px 14px", background: "#EBF5FB", borderRadius: 8, fontSize: 12, color: "#1B3F6E", marginBottom: 16 }}>
            Zoho Journal ID: <strong>{e.zohoJournalId}</strong>
          </div>
        )}

        {/* Messages */}
        {msg   && <div style={{ ...S.msgBox, background: "#E8F5EE", color: "#1A7A4A" }}>{msg}</div>}
        {error && <div style={{ ...S.msgBox, background: "#FFEBEE", color: "#B71C1C" }}>{error}</div>}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {canApprove && e.status === "draft" && (
            <ActionBtn onClick={approve} loading={working === "approve"} color="#1A7A4A">
              ✓ Approve entry
            </ActionBtn>
          )}
          {canApprove && e.status === "approved" && entity?.accountingSoftware?.type === "zoho_api" && (
            <ActionBtn onClick={pushToZoho} loading={working === "push"} color="#1B3F6E">
              Push to Zoho Books
            </ActionBtn>
          )}
          {canApprove && e.status === "push_failed" && (
            <ActionBtn onClick={pushToZoho} loading={working === "push"} color="#C85A00">
              ↻ Retry Zoho push
            </ActionBtn>
          )}
          {isAdmin && !["reversed", "draft"].includes(e.status) && (
            <ActionBtn onClick={reverse} loading={working === "reverse"} color="#B71C1C">
              Reverse entry
            </ActionBtn>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, loading, color, children }) {
  return (
    <button
      onClick={onClick} disabled={loading}
      style={{ padding: "10px 20px", background: loading ? "#90A4AE" : color,
        color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer" }}
    >
      {loading ? "Working…" : children}
    </button>
  );
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}

const S = {
  page:       { maxWidth: 1000, margin: "0 auto" },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  h1:         { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: 0 },
  filterRow:  { display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" },
  filterSelect:{ padding: "8px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit" },
  filterInput: { padding: "8px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit" },
  clearBtn:   { padding: "8px 14px", background: "#fff", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, cursor: "pointer", color: "#607D8B" },
  tableWrap:  { background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  table:      { width: "100%", borderCollapse: "collapse" },
  th:         { padding: "10px 14px", textAlign: "left", background: "#1B3F6E", color: "#fff",
                fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 },
  td:         { padding: "10px 14px", fontSize: 13, color: "#37474F" },
  badge:      { display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600 },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 16, padding: "16px 0" },
  pageBtn:    { padding: "8px 16px", background: "#fff", border: "1.5px solid #CFD8DC", borderRadius: 8,
                fontSize: 13, cursor: "pointer", color: "#1B3F6E" },
  backBtn:    { background: "none", border: "none", color: "#2E86AB", fontSize: 13, cursor: "pointer",
                padding: "0 0 16px", display: "block", fontWeight: 600 },
  detailCard: { background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,.08)" },
  msgBox:     { padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 12 },
};
